import { ChevronRight } from '@mui/icons-material';
import { alpha, Box, styled } from '@mui/material';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BodyText } from '@/components/BodyText';

export type TileEmptyStateAction = { label: string; to: string };

type Tone = 'neutral' | 'positive';

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: '4px',
  padding: '8px 16px',
});

const Graphic = styled(Box, { shouldForwardProp: property => property !== 'tone' })<{ tone: Tone }>(({
  theme,
  tone,
}) => {
  const color = tone === 'positive' ? theme.palette.success.main : theme.palette.secondary.main;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    marginBottom: '8px',
    borderRadius: '50%',
    color,
    backgroundColor: alpha(color, 0.12),
    '& svg': { fontSize: '28px' },
  };
});

const ActionLink = styled(Link)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginTop: '8px',
  fontSize: '0.875rem',
  fontWeight: 600,
  textDecoration: 'none',
  color: theme.palette.secondary.main,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const TileEmptyState = ({
  graphic,
  title,
  description,
  action,
  tone = 'neutral',
  testId,
}: {
  graphic: ReactNode;
  title: string;
  description: string;
  action?: TileEmptyStateAction;
  tone?: Tone;
  testId: string;
}) => (
  <Container data-testid={testId}>
    <Graphic tone={tone} aria-hidden>
      {graphic}
    </Graphic>
    <BodyText fontWeight={600}>{title}</BodyText>
    <BodyText color='text.secondary'>{description}</BodyText>
    {action && (
      <ActionLink to={action.to} data-testid={`${testId}-action`}>
        {action.label}
        <ChevronRight fontSize='small' />
      </ActionLink>
    )}
  </Container>
);
